import { useAppConfig } from "../AppConfigProvider";
import { useGetUserPermissionsQuery } from "../graphql/generated/graphql";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import withAuth from "../hocs/withAuth";
import noAuth from "../hocs/noAuth";
import { useMemo, useRef } from "react";
import { routingInstrumentation } from "../utils/sentry";
import { RolePermissions } from "../constants/rolesAndPermissions";

export type RootStackParamList = {
  "My Day": undefined;
  Home: undefined;
  Productivity: { tab?: string };
  Portfolio: undefined;
  "Student Analytics": undefined;
  Tools: undefined;
  Insights: undefined;
  Journal: undefined;
  Coursework: undefined;
  "Academic Plan": undefined;
  Activities: undefined;
  School: { integration_id: string };
  "Course Catalogue": { integration_id: string };
  BoxSchool: { integration_id: string };
  Course: undefined;
  "Custom Course Student": {
    // resource_type?: string;
    // resource_id?: string;
    course_id?: string;
    integration_id?: string;
    returnToCourseEditor?: boolean;
  };
  "Custom Module Student": {
    module_id: string;
    course_id: string;
    integration_id: string;
  };
  Career: undefined;
  Degree: undefined;
  Content: undefined;
  ContentForm: undefined;
  Modules: undefined;
  Journey: { id: string };
  Project: {
    id: string | null;
    courseId?: string;
    integrationId?: string;
    initialStep?: string;
  };
  CareerDetails: undefined;
  Resource: {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
  };
  "Custom Resource Student": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  "Custom Assignment Student": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  "Custom Course AITutor": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  "Custom Course Assessment": {
    assessment_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  Learn: undefined;
  Categories: undefined;
  Tracks: {
    field: string;
    subfield: string;
  };
  "Track Overview": undefined;
  Lesson: {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
  };
  "Lesson Activities": {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
    loTitle: string;
    loId: string;
  };
  "Lesson Assessment": {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
    loTitle: string;
    loId: string;
  };
  "Lesson Chat": { loId: string };
  "Lesson Video": undefined;
  Health: undefined;
  Onboarding: undefined;
  Settings: undefined;
  Faculty: {
    courseName: string;
    courseId: string;
    integration_id: string;
  };
  Invite: undefined;
  Login: { isLMS: boolean };
  LoginSuperAdmin: { isLMS: boolean };
  LoginLTI: { accessToken?: string; refreshToken?: string };
  LoginSSO: { accessToken?: string; refreshToken?: string; redirect?: string };
  Signup: { isLMS: boolean };
  "Reset Password": { token?: string };
  "Set Password": { token?: string; f?: string; uid?: string; service?: string };
  "Verify Email": undefined;
  ThankYou: undefined;
  ThankYouCoursePayment: undefined;
  EdlinkSuccess: undefined;
  "Custom Coursework": undefined;
  "Custom Coursework Course Form": {
    courseId: string;
  };
  "Custom School Student Courses": { integration_id: string };
  "Custom School Faculty Courses": { integration_id: string };
  "Custom Coursework Modules": {
    courseId: string;
    aiTutorIndex?: number;
    pageIndex?: number;
    moduleIndex?: number;
    courseName?: string;
    parentReturnTo?: string;
    returnTo?: string;
  };
  Admin: undefined;
  "Create Tenant": undefined;
  "Tenant Dashboard": undefined;
  "Tenant Settings": { id: string };
  "OAuth Client Dashboard": undefined;
  Assessment: { assessmentId: string; courseId: string; integrationId: string };
  "Course Settings": { courseId: string; tab?: string };
  "Admin Dashboard": undefined;
  "Manage Courses": undefined;
  Teaching: undefined;
  "Degrees & Programs": undefined;
  "Course Information": {
    courseId: string;
    courseName: string;
    returnTo: string;
    activeTab?: string;
  };
  "Degree Information": {
    degreeId: string;
    degreeName: string;
    returnTo?: string;
    activeTab?: string;
  };
  Students: undefined;
  "Student Profile": { studentId: string; studentName?: string; returnTo?: string };
  "Admin Users": undefined;
  "Admin Grading Center": undefined;
  "Admin Announcements": undefined;
  "Admin Analytics": { studentId?: string };
  "Admin Settings": undefined;
  "Admin Profile": undefined;
  "Session Analytics": undefined;
  "User Session Details": { userId: string };
  Announcement: undefined;
  "Announcement Detail": {
    announcementId: string;
  };
};

type DefaultRoute = keyof RootStackParamList;

const Stack = createStackNavigator<RootStackParamList>();

function pickDefaultRoute(isAxioOffering: boolean, permissions: readonly string[] | undefined): DefaultRoute {
  const adminFacultyPermissions = [
    RolePermissions.courseEdit,
    RolePermissions.analyticsViewFaculty,
    RolePermissions.analyticsViewSchool,
    RolePermissions.facultyView,
    RolePermissions.wildcard,
  ];

  const isAdminOrFaculty = adminFacultyPermissions.some((permission) => permissions.includes(permission));
  if (isAdminOrFaculty) {
    return "Admin Dashboard";
  }

  if (isAxioOffering) return "Home";

  return "My Day";
}

function AppNavigator() {
  const appConfig = useAppConfig();
  const navigation = useRef();

  const { data: userPermissionsData, loading, error } = useGetUserPermissionsQuery();
  const permissions = userPermissionsData?.getUserPermissions || [];

  const currentDomain = appConfig.currentDomain;
  const isLMS = appConfig.isLMS;
  const isAxioOffering = appConfig.isAxioOffering;

  const initialRoute = useMemo<DefaultRoute>(
    () => pickDefaultRoute(isAxioOffering, permissions),
    [isAxioOffering, permissions]
  );

  const linking = {
    prefixes: [`https://${currentDomain}`, "primer://"],
    config: {
      screens: {
        "My Day": "my-day",
        Home: "home",
        Productivity: "productivity",
        Porfolio: "portfolio",
        "Student Analytics": "student-analytics",
        "Academic Plan": "academic-plan",
        Journal: "journal",
        Insights: "student/insights",
        Coursework: "coursework",
        Activities: "activities",
        School: "school",
        "Custom School Faculty Courses": "school/courses",
        Course: "school/course",
        "Custom Coursework Modules": "school/course/modules",
        "Course Settings": "school/course/settings",
        "Custom Coursework": "custom-coursework",
        "Custom Coursework Course Form": "custom-coursework/course-form",
        Resource: "school/course/resource",
        "Custom School Student Courses": "student/school",
        "Custom Course Student": "student/school/course",
        "Custom Resource Student": "student/school/course/resource",
        "Custom Assignment Student": "student/school/course/assignment",
        "Custom Course AITutor": "student/school/course/aitutor",
        "Custom Course Assessment": "student/school/course/assessment",
        Learn: "learn",
        Tracks: "tracks",
        "Track Overview": "track/overview",
        Categories: "categories",
        Lesson: "learn/lesson",
        "Lesson Activities": "learn/lesson/activities",
        "Lesson Assessment": "learn/lesson/assessment",
        "Lesson Chat": "learn/lesson/chat",
        "Lesson Video": "learn/lesson/video",
        Health: "health",
        Onboarding: "onboarding",
        Settings: "settings",
        Faculty: "faculty",
        Invite: "invite",
        Login: "login",
        LoginSuperAdmin: "admin/login",
        LoginLTI: "login/lti",
        LoginSSO: "login/sso",
        Signup: "signup",
        "Reset Password": "reset-password",
        "Set Password": "set-password",
        "Verify Email": "verify-email",
        Career: "career",
        Degree: "degree",
        Content: "content",
        Modules: "content/modules",
        ContentForm: "content/form",
        Journey: "journey",
        Project: "project",
        CareerDetails: "career/details",
        ThankYou: "thank-you",
        ThankYouCoursePayment: "thank-you-course-payment",
        EdlinkSuccess: "edlink-success",

        Admin: "dashboard",
        "Create Tenant": "dashboard/tenant/create",
        "Tenant Dashboard": "dashboard/tenant",
        "Tenant Settings": "dashboard/tenant/settings",
        "OAuth Client Dashboard": "dashboard/oauth-client",

        Assessment: "assessment",

        "Admin Dashboard": "admin/dashboard",
        "Manage Courses": "admin/manage-courses",
        Teaching: "admin/teaching",
        "Degrees & Programs": "admin/degrees-programs",
        "Degree Information": "admin/degree-information",
        "Course Information": "admin/course-information",
        Students: "admin/students",
        "Student Profile": "admin/students/profile",
        "Admin Users": "admin/users",
        "Admin Grading Center": "admin/grading-center",
        "Admin Announcements": "admin/announcements",
        "Admin Analytics": "admin/analytics",
        "Admin Settings": "admin/settings",
        "Admin Profile": "admin/profile",
        "Custom Module Student": "student/school/course/module",
        "Session Analytics": "session-analytics",
        "User Session Details": "session-analytics/user",
        Announcement: "announcement",
        "Announcement Detail": "announcement/detail",
      },
    },
  };

  const DashboardScreen = withAuth(require("../views/DashboardView").default);
  const HomeScreen = withAuth(require("../views/HomeView").default);
  const ProductivityScreen = withAuth(require("../views/ProductivityView").default);
  const PortfolioScreen = withAuth(require("../views/PortfolioView").default);
  const InsightsScreen = withAuth(require("../views/InsightsView").default);
  const StudentAnalyticsScreen = withAuth(require("../views/StudentAnalyticsView").default);
  const JournalScreen = withAuth(require("../views/JournalView").default);
  const SchoolsScreen = withAuth(require("../views/SchoolsView").default);
  const AcademicPlanScreen = withAuth(require("../views/AcademicPlanView").default);
  const SchoolScreen = withAuth(require("../views/SchoolView").default);
  const CustomSchoolStudentCoursesScreen = withAuth(require("../views/CustomSchoolStudentCoursesView").default);
  const CustomSchoolFacultyCoursesScreen = withAuth(require("../views/CustomSchoolFacultyCoursesView").default);
  const CourseScreen = withAuth(require("../views/CourseView").default);
  const CustomCourseStudentScreen = withAuth(require("../views/CustomCourseStudentView").default);
  const CareerScreen = withAuth(require("../views/CareerView").default);
  const DegreeScreen = withAuth(require("../views/DegreeView").default);
  const JourneyScreen = withAuth(require("../views/JourneyView").default);
  const ProjectScreen = withAuth(require("../views/ProjectView").default);
  const CareerDetailScreen = withAuth(require("../views/CareerDetailView").default);
  const ResourceScreen = withAuth(require("../views/ResourceView").default);
  const CustomResourceStudentScreen = withAuth(require("../views/CustomResourceStudentView").default);
  const CustomAssignmentStudentScreen = withAuth(require("../views/CustomAssignmentStudentView").default);
  const CustomCourseAITutorChatScreen = withAuth(require("../views/CustomCourseAITutorChatView").default);
  const CustomCourseAssessmentStudentScreen = withAuth(require("../views/CustomCourseAssessmentStudentView").default);
  const LearnScreen = withAuth(require("../views/LearnView").default);
  const CategoriesScreen = withAuth(require("../views/CategoriesView").default);
  const TracksScreen = withAuth(require("../views/TracksView").default);
  const TrackOverviewScreen = withAuth(require("../views/TrackOverviewView").default);
  const LessonSubchapterScreen = withAuth(require("../views/LessonSubchapterView").default);
  const LessonLearningScreen = withAuth(require("../views/LessonLearningView").default);
  const LessonAssessmentScreen = withAuth(require("../views/LessonAssessmentView").default);
  const LessonChatScreen = withAuth(require("../views/LessonChatView").default);
  const LessonVideoScreen = withAuth(require("../views/LessonVideoView").default);
  const HealthScreen = withAuth(require("../views/HealthView").default);
  const OnboardingScreen = withAuth(require("../views/OnboardingView").default);
  const SettingsScreen = withAuth(require("../views/SettingsView").default);
  const InviteScreen = withAuth(require("../views/InviteView").default);
  const ThankYouScreen = withAuth(require("../views/ThankYouView").default);
  const EdlinkSuccessScreen = withAuth(require("../views/EdlinkSuccessView").default);
  const CustomCourseworkScreen = withAuth(require("../views/CustomCourseworkView").default);
  const FacultyScreen = withAuth(require("../views/FacultyView").default);
  const CustomCourseworkModulesScreen = withAuth(require("../views/CustomCourseworkModulesView").default);
  const CustomCourseworkCourseFormScreen = withAuth(require("../views/CustomCourseworkCourseFormView").default);
  const AdminScreen = withAuth(require("../views/AdminView").default);
  const CreateTenantScreen = withAuth(require("../views/CreateTenantView").default);
  const TenantDashboardScreen = withAuth(require("../views/TenantDashboardView").default);
  const TenantSettingsDashboardScreen = withAuth(require("../views/TenantSettingsDashboardView").default);
  const OAuthClientDashboardScreen = withAuth(require("../views/OAuthClientDashboardView").default);
  const LoginScreen = noAuth(require("../views/LoginView").default);
  const LoginLTIScreen = noAuth(require("../views/LoginLTIView").default);
  const LoginSSOView = noAuth(require("../views/LoginSSOView").default);
  const SignupScreen = noAuth(require("../views/SignupView").default);
  const ResetPasswordScreen = noAuth(require("../views/ResetPasswordView").default);
  const SetPasswordScreen = require("../views/SetPasswordView").default;
  const VerifyEmailScreen = noAuth(require("../views/VerifyEmailView").default);
  const CourseSettingsScreen = withAuth(require("../views/CourseSettingsView").default);
  const AdminDashboardScreen = withAuth(require("../views/adminViews/AdminDashboardView").default);
  const AdminManageCoursesScreen = withAuth(require("../views/adminViews/ManageCoursesView").default);
  const FacultyTeachingScreen = withAuth(require("../views/adminViews/FacultyTeachingView").default);
  const DegreesProgramsScreen = withAuth(require("../views/adminViews/DegreesProgramsView").default);
  const DegreeInformationScreen = withAuth(require("../views/adminViews/DegreeInformationView").default);
  const CourseInformationScreen = withAuth(require("../views/adminViews/CourseInformationView").default);
  const StudentProfileScreen = withAuth(require("../views/adminViews/StudentProfileView").default);
  const StudentsListScreen = withAuth(require("../views/adminViews/StudentsListView").default);
  const AdminUsersScreen = withAuth(require("../views/adminViews/AdminUsersView").default);
  const AdminGradingCenterScreen = withAuth(require("../views/adminViews/GradingCenterView").default);
  const AdminAnnouncementsScreen = withAuth(require("../views/adminViews/AdminAnnouncementsView").default);
  const AdminAnalyticsScreen = withAuth(require("../views/adminViews/AdminAnalyticsView").default);
  const AdminSettingsScreen = withAuth(require("../views/adminViews/AdminSettingsView").default);
  const AdminProfileScreen = withAuth(require("../views/adminViews/AdminProfileView").default);
  const CustomSchoolModuleScreen = withAuth(require("../views/CustomModuleStudentView").default);
  const SessionAnalyticsScreen = withAuth(require("../views/adminViews/SessionAnalyticsView").default);
  const UserSessionDetailsScreen = withAuth(require("../views/adminViews/UserSessionDetailsView").default);
  const AnnouncementView = withAuth(require("../views/AnnouncementView").default);
  const AnnouncemenDetailtView = withAuth(require("../views/AnnouncementDetailView").default);
  if (loading) return null;

  return (
    <NavigationContainer
      linking={linking}
      ref={navigation}
      onReady={() => {
        routingInstrumentation.registerNavigationContainer(navigation);
      }}>
      <Stack.Navigator key={initialRoute} initialRouteName={initialRoute} screenOptions={{ headerShown: false }}>
        <Stack.Screen name="My Day" getComponent={() => DashboardScreen} />
        <Stack.Screen name="Home" getComponent={() => HomeScreen} />
        <Stack.Screen name="Productivity" getComponent={() => ProductivityScreen} />
        <Stack.Screen name="Portfolio" getComponent={() => PortfolioScreen} />
        <Stack.Screen name="Insights" getComponent={() => InsightsScreen} />
        <Stack.Screen name="Student Analytics" getComponent={() => StudentAnalyticsScreen} />
        <Stack.Screen name="Journal" getComponent={() => JournalScreen} />
        <Stack.Screen name="Coursework" getComponent={() => SchoolsScreen} />
        <Stack.Screen name="Academic Plan" getComponent={() => AcademicPlanScreen} />
        <Stack.Screen name="Activities" getComponent={() => SchoolsScreen} />
        <Stack.Screen name="School" getComponent={() => SchoolScreen} />
        <Stack.Screen name="Custom School Student Courses" getComponent={() => CustomSchoolStudentCoursesScreen} />
        <Stack.Screen name="Custom School Faculty Courses" getComponent={() => CustomSchoolFacultyCoursesScreen} />
        <Stack.Screen name="Course" getComponent={() => CourseScreen} />
        <Stack.Screen name="Custom Course Student" getComponent={() => CustomCourseStudentScreen} />
        <Stack.Screen name="Career" getComponent={() => CareerScreen} />
        <Stack.Screen name="Degree" getComponent={() => DegreeScreen} />
        <Stack.Screen name="Journey" getComponent={() => JourneyScreen} />
        <Stack.Screen name="Project" getComponent={() => ProjectScreen} />
        <Stack.Screen name="CareerDetails" getComponent={() => CareerDetailScreen} />
        <Stack.Screen name="Resource" getComponent={() => ResourceScreen} />
        <Stack.Screen name="Custom Resource Student" getComponent={() => CustomResourceStudentScreen} />
        <Stack.Screen name="Custom Assignment Student" getComponent={() => CustomAssignmentStudentScreen} />
        <Stack.Screen name="Custom Course AITutor" getComponent={() => CustomCourseAITutorChatScreen} />
        <Stack.Screen name="Custom Course Assessment" getComponent={() => CustomCourseAssessmentStudentScreen} />
        <Stack.Screen name="Learn" getComponent={() => LearnScreen} />
        <Stack.Screen name="Categories" getComponent={() => CategoriesScreen} />
        <Stack.Screen name="Tracks" getComponent={() => TracksScreen} />
        <Stack.Screen name="Track Overview" getComponent={() => TrackOverviewScreen} />
        <Stack.Screen name="Lesson" getComponent={() => LessonSubchapterScreen} />
        <Stack.Screen name="Lesson Activities" getComponent={() => LessonLearningScreen} />
        <Stack.Screen name="Lesson Assessment" getComponent={() => LessonAssessmentScreen} />
        <Stack.Screen name="Lesson Chat" getComponent={() => LessonChatScreen} />
        <Stack.Screen name="Lesson Video" getComponent={() => LessonVideoScreen} />
        <Stack.Screen name="Health" getComponent={() => HealthScreen} />
        <Stack.Screen name="Onboarding" getComponent={() => OnboardingScreen} />
        <Stack.Screen name="Settings" getComponent={() => SettingsScreen} />
        <Stack.Screen name="Faculty" getComponent={() => FacultyScreen} />
        <Stack.Screen name="Invite" getComponent={() => InviteScreen} />
        <Stack.Screen name="Login" getComponent={() => LoginScreen} initialParams={{ isLMS }} />
        <Stack.Screen name="LoginSuperAdmin" getComponent={() => LoginScreen} initialParams={{ isLMS }} />
        <Stack.Screen name="LoginLTI" getComponent={() => LoginLTIScreen} />
        <Stack.Screen name="LoginSSO" getComponent={() => LoginSSOView} />
        <Stack.Screen name="Signup" getComponent={() => SignupScreen} initialParams={{ isLMS }} />
        <Stack.Screen name="Reset Password" getComponent={() => ResetPasswordScreen} />
        <Stack.Screen name="Set Password" getComponent={() => SetPasswordScreen} />
        <Stack.Screen name="Verify Email" getComponent={() => VerifyEmailScreen} />
        <Stack.Screen name="ThankYou" getComponent={() => ThankYouScreen} />
        <Stack.Screen name="EdlinkSuccess" getComponent={() => EdlinkSuccessScreen} />
        <Stack.Screen name="Custom Coursework" getComponent={() => CustomCourseworkScreen} />
        <Stack.Screen name="Custom Coursework Course Form" getComponent={() => CustomCourseworkCourseFormScreen} />
        <Stack.Screen name="Custom Coursework Modules" getComponent={() => CustomCourseworkModulesScreen} />
        <Stack.Screen name="Admin" getComponent={() => AdminScreen} />
        <Stack.Screen name="Create Tenant" getComponent={() => CreateTenantScreen} />
        <Stack.Screen name="Tenant Dashboard" getComponent={() => TenantDashboardScreen} />
        <Stack.Screen name="Tenant Settings" getComponent={() => TenantSettingsDashboardScreen} />
        <Stack.Screen name="OAuth Client Dashboard" getComponent={() => OAuthClientDashboardScreen} />
        <Stack.Screen name="Course Settings" getComponent={() => CourseSettingsScreen} />
        <Stack.Screen name="Admin Dashboard" getComponent={() => AdminDashboardScreen} />
        <Stack.Screen name="Manage Courses" getComponent={() => AdminManageCoursesScreen} />
        <Stack.Screen name="Teaching" getComponent={() => FacultyTeachingScreen} />
        <Stack.Screen name="Degrees & Programs" getComponent={() => DegreesProgramsScreen} />
        <Stack.Screen name="Students" getComponent={() => StudentsListScreen} />
        <Stack.Screen name="Student Profile" getComponent={() => StudentProfileScreen} />
        <Stack.Screen name="Course Information" getComponent={() => CourseInformationScreen} />
        <Stack.Screen name="Degree Information" getComponent={() => DegreeInformationScreen} />
        <Stack.Screen name="Admin Users" getComponent={() => AdminUsersScreen} />
        <Stack.Screen name="Admin Grading Center" getComponent={() => AdminGradingCenterScreen} />
        <Stack.Screen name="Admin Announcements" getComponent={() => AdminAnnouncementsScreen} />
        <Stack.Screen name="Admin Analytics" getComponent={() => AdminAnalyticsScreen} />
        <Stack.Screen name="Admin Settings" getComponent={() => AdminSettingsScreen} />
        <Stack.Screen name="Admin Profile" getComponent={() => AdminProfileScreen} />
        <Stack.Screen name="Custom Module Student" getComponent={() => CustomSchoolModuleScreen} />
        <Stack.Screen name="Session Analytics" getComponent={() => SessionAnalyticsScreen} />
        <Stack.Screen name="User Session Details" getComponent={() => UserSessionDetailsScreen} />
        <Stack.Screen name="Announcement" getComponent={() => AnnouncementView} />
        <Stack.Screen name="Announcement Detail" getComponent={() => AnnouncemenDetailtView} />
      </Stack.Navigator>
    </NavigationContainer>
  );
}

export default AppNavigator;
